import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  makeStyles, tokens, mergeClasses,
  Body1, Caption1, Spinner, Button, SearchBox,
  DataGrid, DataGridHeader, DataGridHeaderCell, DataGridBody, DataGridRow, DataGridCell,
  createTableColumn,
  type TableColumnDefinition,
} from '@fluentui/react-components';
import { BranchRegular, DataTrendingRegular } from '@fluentui/react-icons';
import { useEpics, useMyProjects } from '../../api/hooks';
import { statusHue } from '../../components/common/StatusPill';
import { HeroProgressMetrics } from '../../components/common/HeroProgressMetrics';
import {
  resolveProgress, sumProgressInputs, progressBasisLabel, formatPoints, type ProgressInput,
} from './EpicProgressPage';
import { brandColors, brandFonts } from '../../theme/brandColors';
import type { EpicSummary } from '../../api/types';

// One epic's weighted-progress ingredients, from the server's pre-summed fields. Used by BOTH the
// hero rollup and the table's Progress column so a row can never disagree with the total above it.
const epicProgressInput = (e: EpicSummary): ProgressInput => ({
  adjustedPoints: e.adjustedPoints ?? 0,
  totalPoints: e.totalPoints ?? 0,
  pointedCount: e.pointedChildCount ?? 0,
  storyCount: e.childCount,
  adjustedCount: e.adjustedCount ?? 0,
});

/* EXECUTIVE-BRIEFING design (per design-reference/executive-briefing.html).
   Navy hero card (kicker → H1 → status-breakdown bars + composite % metric),
   toolbar with search, then a white card holding the epic table: navy
   Plus-Jakarta titles, mono ids, soft status pills, mono progress fraction,
   navy %, brand-blue bar. All four states implemented. */

// Soft (tinted) status pill palette — colour@~16% fill + a readable dark text,
// matching the briefing's `.dva-pill`. Keyed by the shared statusHue() output so
// hero bars and table pills always agree on a state's colour.
type Hue = 'green' | 'lightBlue' | 'purple' | 'blue' | 'red' | 'grey';
const HUE_SOLID: Record<Hue, string> = {
  green: brandColors.green,
  lightBlue: brandColors.lightBlue,
  purple: brandColors.purple,
  blue: brandColors.blue,
  red: brandColors.red,
  grey: brandColors.greyText,
};

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    width: '100%',
    fontFamily: brandFonts.body,
  },

  // ── Hero ──────────────────────────────────────────────────────────────────
  hero: {
    backgroundColor: brandColors.navy,
    color: brandColors.white,
    borderRadius: tokens.borderRadiusXLarge,
    // Design padding ~34/38px — no spacing token that large, so rem.
    padding: '2.125rem 2.375rem 2rem',
    boxShadow: tokens.shadow28,
    overflow: 'hidden',
  },
  kicker: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: brandColors.lightBlue,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    marginBottom: tokens.spacingVerticalM,
  },
  heroTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '-0.01em',
    margin: 0,
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: '1.45fr 1fr',
    gap: tokens.spacingHorizontalXXXL,
    alignItems: 'center',
    marginTop: tokens.spacingVerticalXXL,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr', rowGap: tokens.spacingVerticalXL },
  },
  heroBars: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  barRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  barTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalM },
  barLabel: { fontSize: tokens.fontSizeBase300, color: 'rgba(255,255,255,0.92)' },
  barVal: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },
  heroTrack: {
    height: '0.375rem', // 6px — thinner than any radius/spacing token
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'rgba(255,255,255,0.12)',
    overflow: 'hidden',
  },
  heroFill: { height: '100%', borderRadius: tokens.borderRadiusCircular },

  composite: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)',
    paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  compositeLabel: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: brandColors.lightBlue,
  },
  compositeValue: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  compositeNum: {
    fontFamily: brandFonts.heading,
    fontSize: '3.5rem', // hero metric — larger than any fontSize token
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1',
    color: brandColors.white,
  },
  compositePct: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: brandColors.lightBlue,
  },
  compositeMeta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  // ── Card + table ────────────────────────────────────────────────────────────
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: brandColors.white,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${brandColors.greyLine}`,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${brandColors.greyLine}`,
  },
  search: { width: '24rem', maxWidth: '50%' },
  gridWrap: {
    overflow: 'auto',
    '& [role="row"] > :first-child': { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
    '& [role="row"] > :nth-child(2)': { flexGrow: 0, flexShrink: 0, flexBasis: '9rem' },   // State
    '& [role="row"] > :nth-child(3)': { flexGrow: 0, flexShrink: 0, flexBasis: '12rem' },  // Iteration
    '& [role="row"] > :nth-child(4)': { flexGrow: 0, flexShrink: 0, flexBasis: '5.5rem' }, // UAT
    '& [role="row"] > :nth-child(5)': { flexGrow: 0, flexShrink: 0, flexBasis: '5.5rem' }, // Bugs
    '& [role="row"] > :nth-child(6)': { flexGrow: 0, flexShrink: 0, flexBasis: '15rem' },  // Progress
    // Uppercase, tracked, muted column headers — the briefing's table-foot treatment.
    '& [role="columnheader"]': {
      fontFamily: brandFonts.heading,
      fontSize: tokens.fontSizeBase100,
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: brandColors.greyText,
    },
  },
  rowClickable: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    '&:focus-visible': { outline: `2px solid ${brandColors.blue}`, outlineOffset: '-2px' },
  },
  titleCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  titleText: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em',
    color: brandColors.navy,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  idText: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.02em',
    color: brandColors.greyText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Open UAT / Bug counts — mono numerals; zero is muted so attention lands on the non-zero cells.
  countText: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: brandColors.navy,
    fontVariantNumeric: 'tabular-nums',
  },
  countZero: { color: brandColors.greyText, fontWeight: tokens.fontWeightRegular },
  iterText: { fontFamily: brandFonts.body, color: tokens.colorNeutralForeground2 },

  // Soft status pill
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusCircular,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },

  // Progress
  progressCell: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, minWidth: '11rem' },
  progressTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: tokens.spacingHorizontalS },
  progressFrac: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: brandColors.greyText },
  progressPct: { fontFamily: brandFonts.heading, fontWeight: tokens.fontWeightSemibold, color: brandColors.navy },
  barTrack: {
    width: '100%',
    height: '0.375rem', // 6px
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
    backgroundColor: brandColors.greyLine,
  },
  barFill: { height: '100%', backgroundColor: brandColors.blue, borderRadius: tokens.borderRadiusCircular },

  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderTop: `1px solid ${brandColors.greyLine}`,
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: brandColors.greyText,
  },
  emptyOrError: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
});

function lastSegment(path: string): string {
  return path.split('\\').slice(-1)[0] || path;
}

export function EpicListPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [search, setSearch] = useState('');

  const { data, isLoading, error, refetch } = useEpics(projectId);
  const { data: projects } = useMyProjects();
  const project = projects?.find((p) => p.id === projectId);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((e) =>
      e.title.toLowerCase().includes(q) ||
      (e.customerTitle ?? '').toLowerCase().includes(q) ||
      e.state.toLowerCase().includes(q) ||
      lastSegment(e.iterationPath).toLowerCase().includes(q),
    );
  }, [data, search]);

  // Cumulative progress across the shown epics: the weighted Progress headline, plus its
  // Closed and Resolved parts over child user stories (Removed excluded) — the same definitions
  // the epic-progress page uses, so the two screens always agree.
  const rollup = useMemo(() => {
    const t = filtered.reduce(
      (acc, e) => {
        acc.resolved += e.resolvedChildCount;
        acc.closed += e.closedChildCount;
        acc.discoveryNew += e.discoveryNewChildCount ?? 0;
        acc.workItems += e.workItemCount ?? 0;
        acc.total += e.childCount;
        return acc;
      },
      { resolved: 0, closed: 0, discoveryNew: 0, workItems: 0, total: 0 },
    );
    const pct = (n: number) => (t.total > 0 ? Math.round((n / t.total) * 100) : 0);
    // "New" is a share of ALL work items (any type), not just stories — so its denominator is
    // workItems, not total. This mirrors the drill-down New-scope card.
    const pctNew = t.workItems > 0 ? Math.round((t.discoveryNew / t.workItems) * 100) : 0;
    // Weighted progress: sum the server's raw ingredients across the shown epics, THEN divide.
    // Averaging each epic's percentage would weight a 2-story epic like a 50-story one.
    const progress = resolveProgress(sumProgressInputs(filtered.map(epicProgressInput)));
    return { ...t, pctResolved: pct(t.resolved), pctClosed: pct(t.closed), pctNew, progress };
  }, [filtered]);

  // Hero status breakdown: epics grouped by state, share of the shown set,
  // coloured by the same hue the table pills use.
  const breakdown = useMemo(() => {
    const groups = new Map<string, number>();
    for (const e of filtered) groups.set(e.state, (groups.get(e.state) ?? 0) + 1);
    const total = filtered.length || 1;
    return [...groups.entries()]
      .map(([state, count]) => ({ state, count, pct: count / total, hue: statusHue(state) as Hue }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [filtered]);

  const columns: TableColumnDefinition<EpicSummary>[] = useMemo(() => [
    createTableColumn<EpicSummary>({
      columnId: 'title',
      compare: (a, b) => (a.customerTitle?.trim() || a.title).localeCompare(b.customerTitle?.trim() || b.title),
      renderHeaderCell: () => 'Epic',
      renderCell: (e) => (
        <div className={styles.titleCell}>
          <span className={styles.titleText}>{e.customerTitle?.trim() || e.title}</span>
          <span className={styles.idText}>
            #{e.id}{e.customerTitle?.trim() ? ` · ${e.title}` : ` · ${lastSegment(e.areaPath)}`}
          </span>
        </div>
      ),
    }),
    createTableColumn<EpicSummary>({
      columnId: 'state',
      compare: (a, b) => a.state.localeCompare(b.state),
      renderHeaderCell: () => 'State',
      renderCell: (e) => {
        const hue = statusHue(e.state) as Hue;
        const c = HUE_SOLID[hue];
        return (
          <span className={styles.pill} style={{ backgroundColor: `${c}29`, color: c }}>
            {(e.state || 'Unknown').trim()}
          </span>
        );
      },
    }),
    createTableColumn<EpicSummary>({
      columnId: 'iteration',
      compare: (a, b) => lastSegment(a.iterationPath).localeCompare(lastSegment(b.iterationPath)),
      renderHeaderCell: () => 'Iteration',
      renderCell: (e) => <Caption1 className={styles.iterText}>{lastSegment(e.iterationPath)}</Caption1>,
    }),
    // Open (not Closed/Done/Completed) descendants tagged `uat`, and open Bugs. Independent counts —
    // a UAT-raised bug appears in both. Guarded with ?? 0 so an older API can't blank the row.
    createTableColumn<EpicSummary>({
      columnId: 'uat',
      compare: (a, b) => (a.openUatCount ?? 0) - (b.openUatCount ?? 0),
      renderHeaderCell: () => 'UAT',
      renderCell: (e) => {
        const n = e.openUatCount ?? 0;
        return (
          <span
            className={mergeClasses(styles.countText, n === 0 && styles.countZero)}
            aria-label={`${n} open UAT work item${n === 1 ? '' : 's'}`}
          >
            {n}
          </span>
        );
      },
    }),
    createTableColumn<EpicSummary>({
      columnId: 'bugs',
      compare: (a, b) => (a.openBugCount ?? 0) - (b.openBugCount ?? 0),
      renderHeaderCell: () => 'Bugs',
      renderCell: (e) => {
        const n = e.openBugCount ?? 0;
        return (
          <span
            className={mergeClasses(styles.countText, n === 0 && styles.countZero)}
            aria-label={`${n} open bug${n === 1 ? '' : 's'}`}
          >
            {n}
          </span>
        );
      },
    }),
    createTableColumn<EpicSummary>({
      columnId: 'progress',
      // Weighted progress — the SAME figure the hero totals, so a row can never contradict it.
      compare: (a, b) =>
        resolveProgress(epicProgressInput(a)).percent - resolveProgress(epicProgressInput(b)).percent,
      renderHeaderCell: () => 'Progress',
      renderCell: (e) => {
        const p = resolveProgress(epicProgressInput(e));
        // Show the fraction the percentage is actually built from — a story count next to a
        // points-weighted % reads as a contradiction (12/40 beside 54%).
        const frac = p.basis === 'points'
          ? `${formatPoints(e.adjustedPoints ?? 0)} / ${formatPoints(e.totalPoints ?? 0)} pts`
          : `${formatPoints(e.adjustedCount ?? 0)} / ${e.childCount} stories`;
        return (
          <div className={styles.progressCell}>
            <div className={styles.progressTop}>
              <span className={styles.progressFrac}>{frac}</span>
              <span className={styles.progressPct}>{p.percent}%</span>
            </div>
            <div
              className={styles.barTrack}
              role="img"
              aria-label={`${p.percent}% complete, ${progressBasisLabel(p)}`}
            >
              <div className={styles.barFill} style={{ width: `${p.percent}%` }} />
            </div>
          </div>
        );
      },
    }),
  ], [styles]);

  const total = data?.length ?? 0;
  const shown = filtered.length;

  return (
    <div className={styles.page}>
      {/* Navy executive hero */}
      <section className={styles.hero}>
        <div className={styles.kicker}>
          <DataTrendingRegular fontSize={14} aria-hidden />
          Epics Progress
        </div>
        <h1 className={styles.heroTitle}>{project?.name ?? 'Epics'}</h1>

        <div className={styles.heroGrid}>
          <div className={styles.heroBars}>
            {breakdown.map((b) => (
              <div key={b.state} className={styles.barRow}>
                <div className={styles.barTop}>
                  <span className={styles.barLabel}>{b.state}</span>
                  <span className={styles.barVal}>{b.count} · {Math.round(b.pct * 100)}%</span>
                </div>
                <div className={styles.heroTrack}>
                  <div className={styles.heroFill} style={{ width: `${b.pct * 100}%`, backgroundColor: HUE_SOLID[b.hue] }} />
                </div>
              </div>
            ))}
            {breakdown.length === 0 && <span className={styles.barLabel}>No epics to summarise.</span>}
          </div>

          <HeroProgressMetrics
            percentProgress={rollup.progress.percent}
            percentClosed={rollup.pctClosed}
            percentResolved={rollup.pctResolved}
            progressMeta={`${progressBasisLabel(rollup.progress)} · ${total} epic${total === 1 ? '' : 's'}`}
            closedMeta={`${rollup.closed} / ${rollup.total} closed`}
            resolvedMeta={`${rollup.resolved} / ${rollup.total} resolved`}
            unit="user stories"
            percentNew={rollup.pctNew}
            newMeta={`${rollup.discoveryNew} / ${rollup.workItems} work items added after sign-off`}
          />
        </div>
      </section>

      <div className={styles.container} role="region" aria-label="Epics list">
        <div className={styles.toolbar}>
          <SearchBox
            appearance="underline"
            className={styles.search}
            placeholder="Search epics…"
            value={search}
            onChange={(_e, d) => setSearch(d.value)}
            aria-label="Search epics"
          />
        </div>

        {isLoading ? (
          <div className={styles.emptyOrError}><Spinner size="small" label="Loading epics…" /></div>
        ) : error ? (
          <div className={styles.emptyOrError}>
            <Body1>Couldn't load epics.</Body1>
            <Caption1>{(error as Error).message}</Caption1>
            <Button appearance="primary" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : total === 0 ? (
          <div className={styles.emptyOrError}>
            <BranchRegular fontSize={32} />
            <Body1>No epics found for this project.</Body1>
            <Caption1>Once epics are created in Azure DevOps, they'll appear here.</Caption1>
          </div>
        ) : shown === 0 ? (
          <div className={styles.emptyOrError}>
            <Body1>No epics match "{search}".</Body1>
            <Button appearance="subtle" onClick={() => setSearch('')}>Clear search</Button>
          </div>
        ) : (
          <div className={styles.gridWrap}>
            <DataGrid items={filtered} columns={columns} sortable focusMode="row_unstable" getRowId={(e) => e.id}>
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<EpicSummary>>
                {({ item, rowId }) => {
                  const go = () => navigate(`/reports/projects/${projectId}/epics/${item.id}/progress`);
                  return (
                    <DataGridRow<EpicSummary>
                      key={rowId}
                      className={mergeClasses(styles.rowClickable)}
                      onClick={go}
                      onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
                        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(); }
                      }}
                      aria-label={`Open ${item.title}`}
                    >
                      {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                    </DataGridRow>
                  );
                }}
              </DataGridBody>
            </DataGrid>
          </div>
        )}

        {!isLoading && !error && total > 0 && (
          <div className={styles.footer}>
            <span>
              {shown === total
                ? `${total} epic${total === 1 ? '' : 's'}`
                : `Showing ${shown} of ${total} epic${total === 1 ? '' : 's'}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
